// BatchProgress — one pill per generation batch, from the `batches` array the
// generate workflow status already carries (backend: generation_queue.batch_summary).
// Every poll hands the WHOLE array, terminal batches included, so this component
// never has to remember what it already showed — it just re-renders what it's given.
import { CheckCircle2, Loader2, XCircle, Circle } from "lucide-react";
import { cn } from "@/lib/cn";

function batchState(b) {
  const settled = (b.done || 0) + (b.failed || 0);
  if (b.total > 0 && settled >= b.total) {
    return b.failed > 0 ? "failed" : "done";
  }
  return settled > 0 ? "running" : "queued";
}

export default function BatchProgress({ batches, currentBatch, totalBatches }) {
  if (!Array.isArray(batches) || batches.length === 0) return null;

  return (
    <div className="mt-3">
      <p className="mb-1.5 text-[0.6875rem] font-semibold uppercase tracking-wide text-slate-400">
        Batches{currentBatch ? ` — ${currentBatch}/${totalBatches || batches.length}` : ""}
      </p>
      <ul className="flex flex-wrap gap-1.5">
        {batches.map((b) => {
          const state = batchState(b);
          const isCurrent = currentBatch != null && b.index === currentBatch - 1;
          const title =
            `Batch ${b.index + 1}: ${b.done}/${b.total} generated` +
            (b.failed ? `, ${b.failed} failed` : "") +
            (b.test_case_count ? ` — ${b.test_case_count} test case(s)` : "");
          return (
            <li
              key={b.index}
              title={title}
              className={cn(
                "flex items-center gap-1 rounded-full px-2 py-1 text-[0.6875rem] font-medium",
                state === "done" && "bg-emerald-50 text-emerald-700 dark:bg-emerald-500/10 dark:text-emerald-400",
                state === "failed" && "bg-rose-50 text-rose-600 dark:bg-rose-500/10 dark:text-rose-400",
                state === "running" && "bg-brand-50 text-brand-700 dark:bg-brand-500/10 dark:text-brand-300",
                state === "queued" && "bg-slate-100 text-slate-500 dark:bg-white/5 dark:text-slate-400",
                isCurrent && state !== "done" && state !== "failed" && "ring-1 ring-brand-400",
              )}
            >
              {state === "done" ? <CheckCircle2 size={12} />
                : state === "failed" ? <XCircle size={12} />
                : isCurrent ? <Loader2 size={12} className="animate-spin" />
                : <Circle size={12} />}
              #{b.index + 1} {b.done}/{b.total}
            </li>
          );
        })}
      </ul>
    </div>
  );
}
